/**
 * In-page sub-navigation for the SuperAdmin → Models tier (#1281
 * rename; was Agents pre-#1281).
 *
 * Renders pill links — Assignments, Prompts, Model catalog, Budgets, Agent
 * Memory — under a shared header on every `/admin/models/*` page.
 * Keeps the model-config surfaces visually grouped now that they live
 * under one nav node.
 *
 * Why pills instead of `Tabs` from `@/components/ui`: the destinations
 * are independent routes (each `mount`s its own data loader and
 * unmounts on switch), not panels controlled by local state. `NavLink`
 * keeps `aria-current=page`, deep-linking, and back/forward semantics
 * working without round-tripping through a controlled tab state.
 *
 * **Red-dot badge** — when any model is in the "Not working" state,
 * the Assignments pill gets a small red dot so an operator who lands on
 * a sibling tab (Prompts, etc.) sees the cross-section signal without
 * having to switch to Assignments first. (#1281 follow-up: model health
 * folded INTO the Assignments page — config and health on one screen —
 * so the badge now points there, not at a separate Catalog tab.)
 * Pulled from the same `/api/admin/model-verification` payload the
 * Assignments page reads, so the badge and the page never disagree.
 */
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { adminModelVerificationAPI } from '@/lib/api'

const TABS: ReadonlyArray<{ to: string; label: string; badged?: boolean }> = [
  { to: '/admin/models/assignment', label: 'Assignments', badged: true },
  { to: '/admin/models/prompts', label: 'Prompts' },
  { to: '/admin/models/catalog', label: 'Model catalog' },
  { to: '/admin/models/budgets', label: 'Budgets & caps' },
  { to: '/admin/models/rag', label: 'Agent Memory' },
]

export function AgentsSubNav() {
  const [anyNotWorking, setAnyNotWorking] = useState(false)

  // One-shot fetch on mount — the verification payload is small (a
  // handful of rows) and read on every navigation between sibling tabs.
  // We don't poll; the Assignment page triggers its own fresh load when
  // its "Check now" affordances complete.
  useEffect(() => {
    let cancelled = false
    void adminModelVerificationAPI
      .list()
      .then((r) => {
        if (!cancelled) setAnyNotWorking(r.anyNotWorking)
      })
      .catch(() => {
        // Sub-nav badge is a soft signal — a fetch failure shouldn't
        // throw an error toast at the user. Silently leave the badge
        // off.
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <nav
      aria-label="AI Models sub-navigation"
      className="flex flex-wrap gap-1 border-b pb-2"
    >
      {TABS.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          // `end` so `/admin/models/prompts` doesn't claim the
          // active state when the user is on `/admin/models/prompts/:id`
          // — the detail page renders its own breadcrumb instead.
          end
          className={({ isActive }) =>
            cn(
              'relative text-sm px-3 py-1.5 rounded-md transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          {t.label}
          {t.badged && anyNotWorking && (
            <span
              aria-label="One or more models are not working"
              className="absolute top-1 right-1 inline-block h-2 w-2 rounded-full bg-destructive ring-2 ring-background"
            />
          )}
        </NavLink>
      ))}
    </nav>
  )
}
